import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight, Briefcase, TrendingUp, Award, Shield, GraduationCap,
  DollarSign, Sparkles, ChevronRight, MapPin, BookOpen, Clock, Building,
} from 'lucide-react';
import {
  getCareer, getCountryDefaults, listCareersByCountry, listGlobalGuides,
} from './data/careerData';
import CareerBridgeBlock from './components/CareerBridgeBlock';
import SmartToolWidget from './components/SmartToolWidget';
import ProfileEvaluationCallout from './components/ProfileEvaluationCallout';

const flagEmoji = (code) => {
  if (!code) return '';
  try {
    return String.fromCodePoint(...code.toUpperCase().split('').map((c) => 127397 + c.charCodeAt()));
  } catch { return ''; }
};

// ─── SEO + Schema injection ───
const injectMeta = ({ title, description, breadcrumbs, faqs }) => {
  if (typeof document === 'undefined') return;
  document.title = title;

  const setMeta = (name, content) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };
  setMeta('description', description);

  // Canonical
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', window.location.href);

  // Remove old JSON-LD blocks we injected
  document.querySelectorAll('script[data-sa-jsonld="career"]').forEach((s) => s.remove());

  // Breadcrumb schema
  if (breadcrumbs?.length) {
    const bScript = document.createElement('script');
    bScript.type = 'application/ld+json';
    bScript.setAttribute('data-sa-jsonld', 'career');
    bScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.label,
        item: `${window.location.origin}${b.path}`,
      })),
    });
    document.head.appendChild(bScript);
  }

  // FAQ schema
  if (faqs?.length) {
    const fScript = document.createElement('script');
    fScript.type = 'application/ld+json';
    fScript.setAttribute('data-sa-jsonld', 'career');
    fScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(fScript);
  }
};

const DemandPill = ({ value }) => {
  const tone =
    /critical/i.test(value || '') ? 'bg-red-500/15 text-red-300 border-red-500/30' :
    /very high|high/i.test(value || '') ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
    'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${tone}`}>
      <Sparkles className="w-3 h-3" /> {value}
    </span>
  );
};

const NotFound = () => (
  <div className="min-h-[60vh] flex items-center justify-center px-6 py-20 text-center" data-testid="sa-career-404">
    <div>
      <h1 className="text-3xl font-bold font-syne text-[#003087] mb-3">Career Guide Coming Soon</h1>
      <p className="text-slate-500 max-w-md mx-auto mb-6">
        We&apos;re curating in-depth content for this career path. Browse our published guides or book a 1-1 counselling
        session to get personalised advice now.
      </p>
      <div className="flex items-center justify-center gap-3">
        <Link to="/study-abroad/careers/" className="text-sm font-semibold text-[#003087] hover:text-[#C5A028]">
          Browse all careers →
        </Link>
        <Link to="/study-abroad/book-counselling/?source=career-not-found" className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-5 py-2.5 rounded-full">
          Book Free Counselling <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

const CareerHub = () => {
  const all = useMemo(() => listGlobalGuides(), []);
  // `injectMeta` is a module-level import (stable). Effect must run once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => injectMeta({
    title: 'Careers & Jobs Abroad — ZAFCO Study Abroad',
    description: 'Explore 90+ post-study career pathways across Germany, UK, USA, Canada, Australia, Ireland, New Zealand and Dubai. Salaries, PR-friendly routes & growth forecasts.',
    breadcrumbs: [
      { label: 'Study Abroad', path: '/study-abroad/' },
      { label: 'Careers & Jobs', path: '/study-abroad/careers/' },
    ],
  }), []);
  return (
    <div data-testid="sa-career-hub">
      <section className="sa-hero-gradient py-16 lg:py-20 text-center">
        <div className="max-w-4xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <Briefcase className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Career Intelligence</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-syne text-white mb-4">
            Careers &amp; Jobs <span className="text-[#C5A028]">Abroad</span>
          </h1>
          <p className="text-base text-slate-300 max-w-2xl mx-auto">
            90+ career guides spanning post-study work visas, PR-friendly fields, salaries and growth forecasts across the world&apos;s top study destinations.
          </p>
        </div>
      </section>
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold font-syne text-[#003087] mb-6">Global Career Guides</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {all.map((g) => (
                <Link key={g.slug} to={`/study-abroad/careers/${g.slug}/`}
                  className="block p-5 border border-slate-200 hover:border-[#C5A028] rounded-xl transition-colors"
                  data-testid={`career-card-${g.slug}`}
                >
                  <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">{g.tagline}</div>
                  <div className="text-base font-semibold text-slate-900">{g.title}</div>
                  <div className="flex items-center gap-1 mt-2 text-xs font-semibold text-[#003087]">
                    Read guide <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <aside className="lg:col-span-1 lg:sticky lg:top-24 h-fit">
            <SmartToolWidget variant="sidebar" />
          </aside>
        </div>
      </section>
    </div>
  );
};

const DynamicCareerPage = () => {
  const params = useParams();
  const country = params.country || null;
  const slug = params.slug || null;
  const isHub = !slug && !country;

  const career = !isHub ? getCareer(country, slug) : null;
  const defaults = country ? getCountryDefaults(country) : null;
  const isGlobal = !country;

  const breadcrumbs = useMemo(() => {
    const b = [
      { label: 'Study Abroad', path: '/study-abroad/' },
      { label: 'Careers & Jobs', path: '/study-abroad/careers/' },
    ];
    if (country && defaults) b.push({ label: defaults.name, path: `/study-abroad/careers/${country}/` });
    if (career) b.push({ label: career.title, path: typeof window !== 'undefined' ? window.location.pathname : '' });
    return b;
  }, [country, defaults, career]);

  useEffect(() => {
    if (isHub || !career) return;
    const metaDesc = `${career.title} — ${career.tagline || ''} ${career.intro || ''}`.slice(0, 200);
    injectMeta({
      title: `${career.title} | ZAFCO Study Abroad`,
      description: metaDesc,
      breadcrumbs,
      faqs: career.faqs || [],
    });
  }, [career, breadcrumbs, isHub]);

  // Hub route /careers/
  if (isHub) return <CareerHub />;

  if (!career) return <NotFound />;

  // Related: same country, different slug
  const related = country ? listCareersByCountry(country).filter((c) => c.slug !== slug).slice(0, 6) : [];

  const ctaHref = `/study-abroad/book-counselling/?source=study-abroad-career-pseo&country=${encodeURIComponent(defaults?.name || '')}&career=${encodeURIComponent(career.title)}`;

  return (
    <div data-testid={`sa-career-${country || 'global'}-${slug}`}>
      {/* Breadcrumbs (visual) */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex flex-wrap items-center gap-1 text-xs text-slate-500">
          {breadcrumbs.map((b, i) => (
            <span key={b.path + i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3 h-3 text-slate-300" />}
              {i === breadcrumbs.length - 1 ? (
                <span className="text-slate-700 font-medium">{b.label}</span>
              ) : (
                <Link to={b.path} className="hover:text-[#003087]">{b.label}</Link>
              )}
            </span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="sa-hero-gradient relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-80 h-80 bg-[#C5A028] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20">
          <div className="flex items-center gap-2 mb-4">
            {!isGlobal && defaults && (
              <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs text-white/90">
                <span className="text-base leading-none">{flagEmoji(defaults.flag)}</span>
                {defaults.name}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 bg-[#C5A028]/15 border border-[#C5A028]/40 text-[#FACC15] rounded-full px-3 py-1 text-xs font-semibold">
              <Briefcase className="w-3 h-3" /> Career Guide
            </span>
            {career.skillShortage && (
              <span className="inline-flex items-center gap-1.5 bg-red-500/15 border border-red-500/40 text-red-300 rounded-full px-3 py-1 text-xs font-semibold">
                <Award className="w-3 h-3" /> Skill Shortage
              </span>
            )}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-syne text-white mb-4 leading-tight">
            {career.title}
          </h1>
          <p className="text-base text-slate-300 max-w-3xl mb-6">{career.tagline}</p>
          <div className="flex flex-wrap items-center gap-3">
            <Link to={ctaHref}
              className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3.5 rounded-full sa-pulse-cta"
              data-testid="career-cta-counselling"
            >
              Book Free Career Counselling <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/study-abroad/visa-eligibility/"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 text-white text-sm font-semibold px-5 py-3.5 rounded-full"
            >
              Check Visa Eligibility
            </Link>
          </div>
        </div>
      </section>

      {/* Intro + Stats grid */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="text-lg font-bold font-syne text-[#003087] mb-3">
              Why Choose {career.title.replace(' in ' + (defaults?.name || ''), '')}{defaults ? ` in ${defaults.name}` : ' Abroad'}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">{career.intro}</p>
            {career.body && <p className="text-sm text-slate-600 leading-relaxed mb-5">{career.body}</p>}

            {/* Market drivers */}
            <h3 className="text-base font-bold font-syne text-[#003087] mb-3 mt-8">Market Drivers &amp; Statistics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="border border-slate-200 rounded-xl p-4">
                <TrendingUp className="w-4 h-4 text-[#C5A028] mb-2" />
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Industry Demand</div>
                <div className="text-sm font-semibold text-slate-900 mt-1"><DemandPill value={career.demand} /></div>
              </div>
              <div className="border border-slate-200 rounded-xl p-4">
                <Building className="w-4 h-4 text-[#C5A028] mb-2" />
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Top Employers</div>
                <div className="text-sm font-medium text-slate-700 mt-1 leading-snug">{career.opportunities || (career.countries || []).join(', ')}</div>
              </div>
              <div className="border border-slate-200 rounded-xl p-4">
                <DollarSign className="w-4 h-4 text-[#C5A028] mb-2" />
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Salary Range</div>
                <div className="text-sm font-semibold text-slate-900 mt-1">{career.salary || defaults?.avgSalaryBand || 'Varies'}</div>
              </div>
            </div>

            {/* Immigration */}
            {!isGlobal && defaults && (
              <>
                <h3 className="text-base font-bold font-syne text-[#003087] mb-3 mt-8">Immigration &amp; PR Pathway</h3>
                <div className="space-y-3">
                  <div className="border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                    <Clock className="w-4 h-4 text-[#C5A028] mt-1 shrink-0" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400">Post-Study Work Permit</div>
                      <div className="text-sm font-medium text-slate-800 mt-1">{defaults.psw}</div>
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-4 flex items-start gap-3">
                    <Shield className="w-4 h-4 text-[#C5A028] mt-1 shrink-0" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400">PR / Residency Pathway</div>
                      <div className="text-sm font-medium text-slate-800 mt-1">{defaults.pr}</div>
                      {career.prTip && <div className="text-xs text-slate-500 mt-1">💡 {career.prTip}</div>}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Academic Gateway */}
            {!isGlobal && defaults && (
              <>
                <h3 className="text-base font-bold font-syne text-[#003087] mb-3 mt-8">Academic Gateway</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="border border-slate-200 rounded-xl p-4">
                    <GraduationCap className="w-4 h-4 text-[#C5A028] mb-2" />
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">Top Universities</div>
                    <div className="text-sm text-slate-700 mt-1 leading-snug">{defaults.topUnis.join(' · ')}</div>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-4">
                    <BookOpen className="w-4 h-4 text-[#C5A028] mb-2" />
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">Recommended Courses</div>
                    <div className="text-sm text-slate-700 mt-1 leading-snug">{(career.topCourses || []).join(' · ')}</div>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-4">
                    <DollarSign className="w-4 h-4 text-[#C5A028] mb-2" />
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">Approx Tuition</div>
                    <div className="text-sm font-medium text-slate-800 mt-1">{career.tuition || '—'}</div>
                  </div>
                  <div className="border border-slate-200 rounded-xl p-4">
                    <Award className="w-4 h-4 text-[#C5A028] mb-2" />
                    <div className="text-[11px] uppercase tracking-wider text-slate-400">Eligibility (IELTS / PTE)</div>
                    <div className="text-sm font-medium text-slate-800 mt-1">{defaults.ielts}</div>
                  </div>
                </div>
              </>
            )}

            {/* Growth Forecast */}
            <h3 className="text-base font-bold font-syne text-[#003087] mb-3 mt-8">Growth Forecast</h3>
            <div className="bg-gradient-to-r from-[#003087]/5 to-[#C5A028]/5 border border-[#C5A028]/30 rounded-xl p-5 flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-[#C5A028] mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-slate-900">{career.growthForecast || 'Positive trend'}</div>
                <div className="text-xs text-slate-500 mt-1">Updated for 2026 hiring season.</div>
              </div>
            </div>

            {/* FAQs */}
            {(career.faqs || []).length > 0 && (
              <>
                <h3 className="text-base font-bold font-syne text-[#003087] mb-3 mt-8">Frequently Asked Questions</h3>
                <div className="space-y-2">
                  {career.faqs.map((f) => (
                    <details key={f.q} className="border border-slate-200 rounded-xl p-4 group">
                      <summary className="text-sm font-semibold text-slate-900 cursor-pointer list-none flex items-center justify-between">
                        {f.q}
                        <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                      </summary>
                      <p className="text-sm text-slate-600 leading-relaxed mt-3">{f.a}</p>
                    </details>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Right rail — quick stats + CTA */}
          <aside className="lg:col-span-1 space-y-5">
            <div className="bg-gradient-to-br from-[#003087] to-[#001F5C] rounded-2xl p-6 text-white">
              <h4 className="text-base font-bold font-syne mb-1">Free Career Counselling</h4>
              <p className="text-xs text-slate-300 mb-4">
                Get a 1-on-1 session with a ZAFCO advisor specialised in {defaults?.name || 'global'} careers.
              </p>
              <Link to={ctaHref}
                className="block text-center bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3 rounded-full transition-all"
              >
                Book Now <ArrowRight className="w-4 h-4 inline ml-1" />
              </Link>
            </div>

            {!isGlobal && defaults && (
              <div className="border border-slate-200 rounded-2xl p-5 bg-white">
                <h4 className="text-sm font-bold font-syne text-[#003087] mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#C5A028]" /> {defaults.name} Snapshot
                </h4>
                <div className="space-y-2 text-xs">
                  <div><span className="text-slate-500">Avg salary band:</span> <span className="font-medium text-slate-800">{defaults.avgSalaryBand}</span></div>
                  <div><span className="text-slate-500">PSW:</span> <span className="font-medium text-slate-800">{defaults.psw}</span></div>
                  <div><span className="text-slate-500">Currency:</span> <span className="font-medium text-slate-800">{defaults.currency}</span></div>
                </div>
              </div>
            )}

            {/* Smart Tool widget (3-question micro funnel) */}
            <SmartToolWidget variant="sidebar" />
          </aside>
        </div>
      </section>

      {/* Related careers */}
      {related.length > 0 && (
        <section className="py-14 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-5">Related Careers in {defaults?.name}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/study-abroad/careers/${country}/${r.slug}/`}
                  className="block bg-white p-4 border border-slate-200 hover:border-[#C5A028] rounded-xl transition-colors"
                >
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">{defaults?.name}</div>
                  <div className="text-sm font-semibold text-slate-900">{r.title}</div>
                  <div className="flex items-center gap-1 mt-2 text-xs font-semibold text-[#003087]">
                    Explore <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Profile Evaluation Callout — drives evaluated leads from career pages */}
      <section className="bg-white py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-6">
          <ProfileEvaluationCallout
            context="career"
            countryHint={country}
            sourceVariant="career-prefooter"
          />
        </div>
      </section>

      {/* Career-Bridge Block — ZAFCO Global Network footer (Phase 5.9) */}
      <CareerBridgeBlock context="career" countryName={defaults?.name || ''} />
    </div>
  );
};

export default DynamicCareerPage;
